import React, { useEffect, useMemo, useState } from 'react';
import {
    Box, Typography, Button, IconButton, Card, Stack, Chip,
    Pagination, Select, MenuItem, FormControl,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { initializeDB } from '../../../config/db';
import { SRD_INSPECTION_STORE } from '../../../config/constants';

export default function SrdInspectionList() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    useEffect(() => { load(); }, []);

    async function load() {
        setLoading(true);
        try {
            const db = await initializeDB();
            if (db.objectStoreNames.contains(SRD_INSPECTION_STORE)) {
                const all = await db.transaction(SRD_INSPECTION_STORE, 'readonly')
                    .objectStore(SRD_INSPECTION_STORE).getAll();
                setItems((all || [])
                    .filter(f => f.status !== 'submitted')
                    .sort((a, b) => new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0)));
            }
        } catch (e) {
            console.error('Error loading SRD inspections:', e);
        } finally {
            setLoading(false);
        }
    }

    const paginatedItems = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return items.slice(start, start + itemsPerPage);
    }, [items, currentPage, itemsPerPage]);

    const totalPages = Math.ceil(items.length / itemsPerPage);

    const handleDelete = async (uuid) => {
        if (!window.confirm('Delete this draft?')) return;
        try {
            const db = await initializeDB();
            await db.transaction(SRD_INSPECTION_STORE, 'readwrite').objectStore(SRD_INSPECTION_STORE).delete(uuid);
            await load();
        } catch (e) { console.error(e); }
    };

    const cardTitle = (rec) => {
        const parts = [];
        if (rec.toolPart) parts.push(rec.toolPart);
        if (rec.toolSerialNo) parts.push(`S/N: ${rec.toolSerialNo}`);
        if (rec.toolManufacturer) parts.push(rec.toolManufacturer);
        return parts.length > 0 ? parts.join(' — ') : rec.uuid;
    };

    return (
        <Box sx={{ height: 'calc(100vh - 60px)', backgroundColor: '#f5f5f5', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Box sx={{ backgroundColor: 'white', borderBottom: '1px solid #e0e0e0', p: 1.5, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton onClick={() => navigate('/main')} edge="start"><ArrowBackIcon /></IconButton>
                        <Typography variant="h6" fontWeight={600}>SRD Inspection</Typography>
                    </Box>
                    <Button variant="contained" startIcon={<AddIcon />}
                        onClick={() => navigate('/operations/srd-inspection/form')}
                        sx={{ backgroundColor: '#1565c0', '&:hover': { backgroundColor: '#0d47a1' } }}>
                        New Inspection
                    </Button>
                </Box>
            </Box>

            <Box sx={{ p: 1.5, flexGrow: 1, overflowY: 'auto' }}>
                {loading ? (
                    <Card sx={{ p: 4 }}><Typography>Loading...</Typography></Card>
                ) : items.length === 0 ? (
                    <Card sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="h6" color="text.secondary">No drafts found</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Click "New Inspection" to create one</Typography>
                    </Card>
                ) : (
                    <>
                        <Stack spacing={1}>
                            {paginatedItems.map((rec) => (
                                <Card key={rec.uuid} sx={{ p: 1.5 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Box sx={{ flex: 1 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                <Typography variant="subtitle1" sx={{ fontSize: '0.95rem' }}>{cardTitle(rec)}</Typography>
                                                <Chip label="Draft" size="small" sx={{ backgroundColor: '#ff9800', color: 'white', height: 20 }} />
                                                {rec.overallResult && (
                                                    <Chip label={rec.overallResult === 'pass' ? 'PASS' : 'FAIL'} size="small"
                                                        sx={{ backgroundColor: rec.overallResult === 'pass' ? '#4caf50' : '#f44336', color: 'white', height: 20 }} />
                                                )}
                                            </Box>
                                            <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
                                                {rec.jobCode && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        <strong>Job:</strong> {rec.jobCode}{rec.customerName ? ` — ${rec.customerName}` : ''}
                                                    </Typography>
                                                )}
                                                {rec.inspectionDate && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        <strong>Date:</strong> {rec.inspectionDate}
                                                    </Typography>
                                                )}
                                            </Stack>
                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                                                Updated: {new Date(rec.updatedAt || rec.createdAt).toLocaleString()}
                                            </Typography>
                                        </Box>
                                        <Stack direction="row" spacing={0.5}>
                                            <IconButton size="small" color="primary"
                                                onClick={() => navigate(`/operations/srd-inspection/form?uuid=${encodeURIComponent(rec.uuid)}`)}>
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton size="small" color="error" onClick={() => handleDelete(rec.uuid)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        </Stack>
                                    </Box>
                                </Card>
                            ))}
                        </Stack>

                        {items.length > itemsPerPage && (
                            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, items.length)} of {items.length}
                                    </Typography>
                                    <FormControl size="small">
                                        <Select value={itemsPerPage} onChange={(e) => { setItemsPerPage(e.target.value); setCurrentPage(1); }} sx={{ minWidth: 80 }}>
                                            <MenuItem value={10}>10</MenuItem>
                                            <MenuItem value={25}>25</MenuItem>
                                            <MenuItem value={50}>50</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Box>
                                <Pagination count={totalPages} page={currentPage} onChange={(_, v) => setCurrentPage(v)} color="primary" size="medium" showFirstButton showLastButton />
                            </Box>
                        )}
                    </>
                )}
            </Box>
        </Box>
    );
}
